import React, { useEffect, useState } from "react";
import { Collapse, useTheme, Grid } from "@material-ui/core";
import { toast } from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";

import SoftBox from "../../../../components/SoftBox";
import SoftButton from "../../../../components/SoftButton";
import SoftInput from "../../../../components/SoftInput";
import SoftTypography from "../../../../components/SoftTypography";
import { PlayerMemoSchema } from "../../../../form-validations";
import { UseUserId } from "../../../../hooks/auth";
import { refetchUserData } from "../../../../redux/auth/actions";
import { UseSelectedWorkspace } from "../../../../hooks/company";
import { GET_USER_BY_GROUPID } from "../../../../api/user";

import {
  ADD_WIDGETS_TO_LIST,
  GET_WIDGETS_LIST,
  DELETE_WIDGET_FROM_LIST,
} from "../../../../api/userWidgets";

const MyLevel = ({ widget, closeModal, widgetStatus, isUpdated, deleteModal, memberId, deleteStatus, resetDeleteStatus, setReFetch, onUsersMarkedForDeletion }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [options, setOptions] = useState([]);
  const [forUpdate, setforUpdated] = useState(false);
  const [usersMarkedForDeletion, setUsersMarkedForDeletion] = useState([]);

  const { _id: groupId } = UseSelectedWorkspace();
  const theme = useTheme();
  const userId = UseUserId();
  const dispatch = useDispatch();

  const [playerSearch, setPlayerSearch] = useState("");
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);

  const fetchUsersByGroupId = async () => {
    setLoading(true);
    try {
      let filteredUsers = [];

      const { data } = await GET_USER_BY_GROUPID(groupId);
      const result = await GET_WIDGETS_LIST({
        key: widget.key,
        companyId: null,
        userId: null,
      });

      filteredUsers = data.filter(
        (user) =>
          user._id !== userId &&
          !user.isArchived &&
          user.companyId !== null &&
          user.logins > 0
      );

      // filter users with playerSearch if applied
      const applyPlayerSearch = (usersArr) => {
        if (!playerSearch || typeof playerSearch !== "string") return usersArr;
        const lowered = playerSearch.trim().toLowerCase();
        if (lowered === "") return usersArr;
        return usersArr.filter((user) =>
          (`${user.firstName} ${user.lastName}`.toLowerCase().includes(lowered) ||
           user.email?.toLowerCase().includes(lowered))
        );
      };

      if (result.data.length) {
        const optionElements = [];
        const existingUsers = [];

        // filter users by search before further processing
        const filteredAndSearched = applyPlayerSearch(filteredUsers);

        filteredAndSearched.forEach((user) => {
          const isUserExist = result.data.find(
            (item) => item.userId === user._id
          );

          const username = `${user.firstName} ${user.lastName}`;

          if (isUserExist) {
            const payload = {
              ...user,
              value: user._id,
              label: username,
              assignedValue: isUserExist.userInput,
            };
            existingUsers.push(payload);
          } else {
            const payload = {
              ...user,
              value: user._id,
              label: username,
            };
            optionElements.push(payload);
          }
        });

        setOptions(optionElements);
        setUsers(existingUsers);
        if (existingUsers.length > 0) {
          setforUpdated(true);
        }
      } else {
        // filter users by search before mapping to options
        const searchedOptions = applyPlayerSearch(filteredUsers).map((item) => {
          const username = `${item.firstName} ${item.lastName}`;
          return {
            ...item,
            value: item._id,
            label: username,
          };
        });

        setOptions([...searchedOptions]);
      }
    } catch (error) {
      console.log(error?.response);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsersByGroupId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerSearch]);

  const {
    primary: { main: primaryColor },
  } = theme.palette;

  const handleChange = (e, index) => {
    const { value } = e.target;
    const list = [...users];
    list[index].assignedValue = value;
    setUsers([...list]);
  };

  const validationOpt = {
    resolver: yupResolver(PlayerMemoSchema),
    shouldUnregister: false,
    defaultValues: {
      title: widget.title,
    },
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm(validationOpt);

  const triggerSave = async (formData) => {
    const { title } = formData;
    setLoading(true);
    try {
      await Promise.all(
        users.map((user) => {
          const payload = {
            userId: user._id,
            key: widget.key,
            category: widget.category,
            title,
            userInput: user.assignedValue,
          };
          return ADD_WIDGETS_TO_LIST(payload);
        }),
      );
      dispatch(refetchUserData(userId));
      
      // If there are users marked for deletion, notify parent and close modal
      if (usersMarkedForDeletion.length > 0) {
        // Notify parent about users marked for deletion before closing
        if (onUsersMarkedForDeletion && typeof onUsersMarkedForDeletion === 'function') {
          onUsersMarkedForDeletion(usersMarkedForDeletion);
        }
        closeModal();
        // Use setTimeout to ensure modal closes before opening delete modal
        setTimeout(() => {
          if (deleteModal && typeof deleteModal === 'function') {
            // Open delete modal for the first user (or handle multiple users as needed)
            deleteModal(usersMarkedForDeletion[0]);
          }
        }, 100);
        setLoading(false);
        return;
      }
      
      setTimeout(() => {
        if(widgetStatus === "Activated"){
          toast.success("Widget updated successfully");
          if(isUpdated && typeof isUpdated === 'function'){
            isUpdated();
          }
        }else{
          toast.success("Widget activated successfully");
          setReFetch(2);
        }
        closeModal();
      }, 1000);
    } catch (error) {
      console.log(error.response);
    }
    setLoading(false);
  };

  const [groupWidgets, setGroupWidgets] = useState([]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await GET_WIDGETS_LIST({
          key: widget.key,
          companyId: null,
          userId: null,
        });
        setGroupWidgets(result.data);
      } catch (error) {
        console.error("Error fetching group users and widgets:", error);
      }
    };

    if (groupId && widget?.key) {
      fetchData();
    }
  }, [groupId, widget?.key]);

  const handleRemove = async (widgetId, playerId, active) => {
    if (active) {
      try {
        const widgetToRemove = groupWidgets.find(
          (item) => item.userId === playerId && item.key === widget.key,
        );
        await DELETE_WIDGET_FROM_LIST(widgetToRemove?._id, playerId);
        dispatch(refetchUserData(userId));
        // Check if there are remaining users after deletion
        const remainingResult = await GET_WIDGETS_LIST({
          key: widget.key,
          companyId: null,
          userId: null,
        });
        const hasRemainingUsers = remainingResult.data.length > 0;
        if (resetDeleteStatus && typeof resetDeleteStatus === 'function') {
          resetDeleteStatus();
        }
        setTimeout(() => {
          toast.success(hasRemainingUsers ? "Widget updated successfully" : "Widget deactivated successfully");
          setReFetch(2);
          closeModal();
        }, 1000);
      } catch (error) {
        console.log("Error while deactivating the widget", error);
      }
    }else{
      setUsers((prevUsers) => prevUsers.filter((user) => user._id !== playerId));
    }
  };

  useEffect(() => {
    if(deleteStatus && memberId){
      handleRemove(widget._id, memberId, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteStatus, memberId]);

  return (
    <form onSubmit={handleSubmit(triggerSave)}>
      <SoftInput
        {...register("title")}
        placeholder="Enter Title"
        error={!!errors?.title}
        helperText={errors?.title?.message}
        mb="30px"
        className="text-black mb-3"
        sx={{
          // light glass input
          border: "1px solid #E4E7EC",
          backgroundColor: "#F6F7F9 !important",
          color: "#1d2939 !important",
          "& .MuiInputBase-input::placeholder": {
            color: "#98A2B3 !important",
            opacity: "1",
          },
          "&.Mui-focused": {
            borderColor: primaryColor,
            boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
          },
        }}
      />
      <SoftBox>
        <SoftInput
          placeholder="Select Player"
          value={playerSearch || ""}
          onChange={(e) => {
            setPlayerSearch(e.target.value);
            if (!showPlayerDropdown) setShowPlayerDropdown(true);
          }}
          sx={{
            position: "relative",
            mb: "0rem",
            // light glass input
            backgroundColor: "#F6F7F9 !important",
            border: "1px solid #E4E7EC",
            borderRadius: "8px",
            padding: "0.5rem 1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "14px",
            lineHeight: "120%",
            color: "#1d2939 !important",
            "& .MuiInputBase-input::placeholder": {
              color: "#98A2B3 !important",
              opacity: "1",
            },
            "&.Mui-focused": {
              borderColor: "none!important",
              boxShadow: `none!important`,
            },
          }}
          onClick={() => setShowPlayerDropdown((prev) => !prev)}
        />
        {!showPlayerDropdown ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              position: "absolute",
              right: "32px",
              top: "142px",
              margin: 0,
            }}
          >
            <path d="M7.41 8.58L12 13.17L16.59 8.58L18 10L12 16L6 10L7.41 8.58Z" fill="#6C757D"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
            style={{
              position: "absolute",
              right: "32px",
              top: "142px",
              margin: 0,
            }}>
            <path d="M16.59 15.42L12 10.83L7.41 15.42L6 14L12 8L18 14L16.59 15.42Z" fill="#6C757D"/>
          </svg>
        )}
      </SoftBox>
      <Collapse in={!!showPlayerDropdown || false} sx={{ position: "absolute", left: 0, right: 0, zIndex: 20 }}>
        <SoftBox
          sx={{
            mt: "6px",
            // light glass surface
            background: "#FFFFFF",
            border: "1px solid #E4E7EC",
            borderRadius: "8px",
            padding: "0.5rem",
            maxHeight: 220,
            overflowY: "auto",
            minWidth: "100%",
          }}
        >
          {!loading && options && options.length > 0 && (
            options.map((option) => {
              const checked = users.some((u) => u.value === option.value);
              return (
                <SoftBox
                  key={option.value}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={1}
                  py={0.7}
                >
                  <SoftTypography
                    sx={{ color: "#344054", fontSize: "15px", opacity: 1 }}
                  >
                    {option.label}
                  </SoftTypography>
                  {!checked ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        let next;
                        if (checked) {
                          next = users.filter((u) => u.value !== option.value);
                        } else {
                          next = [...users, option];
                        }
                        setUsers(next);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <path d="M0 3C0 1.34315 1.34315 0 3 0H13C14.6569 0 16 1.34315 16 3V13C16 14.6569 14.6569 16 13 16H3C1.34315 16 0 14.6569 0 13V3Z" fill="#0F172A" fillOpacity="0.03"/>
                      <path d="M3 0.5H13C14.3807 0.5 15.5 1.61929 15.5 3V13C15.5 14.3807 14.3807 15.5 13 15.5H3C1.61929 15.5 0.5 14.3807 0.5 13V3C0.5 1.61929 1.61929 0.5 3 0.5Z" stroke="#0F172A" strokeOpacity="0.12"/>
                    </svg>
                  ) : ( 
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        // Unselect user when checked icon is clicked
                        const next = users.filter((u) => u.value !== option.value);
                        setUsers(next);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <path d="M0 3C0 1.34315 1.34315 0 3 0H13C14.6569 0 16 1.34315 16 3V13C16 14.6569 14.6569 16 13 16H3C1.34315 16 0 14.6569 0 13V3Z" fill="url(#paint0_linear_397_6534)"/>
                      <path d="M3 0.5H13C14.3807 0.5 15.5 1.61929 15.5 3V13C15.5 14.3807 14.3807 15.5 13 15.5H3C1.61929 15.5 0.5 14.3807 0.5 13V3C0.5 1.61929 1.61929 0.5 3 0.5Z" stroke="#0F172A" strokeOpacity="0.12"/>
                      <path d="M6.57083 11.5L3.24583 8.175L4.07708 7.34375L6.57083 9.8375L11.9229 4.48541L12.7542 5.31666L6.57083 11.5Z" fill="#FFFCFC"/>
                      <defs>
                        <linearGradient id="paint0_linear_397_6534" x1="12.9922" y1="63.4423" x2="-3.95404" y2="62.3679" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#7928CA"/>
                          <stop offset="1" stopColor="#FF0080"/>
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                </SoftBox>
              );
            })
          )}
          <SoftBox mt={1} display="flex" justifyContent="flex-end">
            <SoftButton
              size="small"
              color="info"
              variant="contained"
              onClick={() => {
                setShowPlayerDropdown(false);
              }}
              sx={{
                px: "2rem",
                py: "0rem!important",
                background: "#F9FAFB",
                boxShadow: "0px 0.320293px 0.640586px rgba(0, 0, 0, 0.05)",
                borderRadius: "2px",
                fontSize: "10px",
                lineHeight: "12px",
                fontWeight: "700",
                color: "#1D2939",
                ":hover": {
                  background: "#F9FAFB",
                },
              }}
            >
              Select
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </Collapse>

      <Collapse in={users.length}>
        <SoftBox marginY="1rem" sx={{ paddingX: "0.5rem!important", marginTop: "2rem!important" }}>
          {users.map((user, index) => (
            <Grid
              container
              spacing={2}
              style={{ marginBottom: "1rem", padding: "0.25rem", border: "1px solid rgba(152, 162, 179, 0.3)", borderRadius: "8px" }}
              key={user._id}
            >
              <Grid item md={6}>
                <SoftInput
                  placeholder="Player Name"
                  type="text"
                  name="playerName"
                  value={user.label}
                  disabled
                  sx={{
                    // light glass input
                    border: "1px solid #E4E7EC",
                    backgroundColor: "#F6F7F9 !important",
                    color: "#1d2939 !important",
                    "& .MuiInputBase-input::placeholder": {
                      color: "#98A2B3 !important",
                      opacity: "1",
                    },
                    "&.Mui-focused": {
                      borderColor: primaryColor,
                      boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                    },
                  }}
                />
              </Grid>
              <Grid item md={6}>
                <SoftBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                  <SoftBox sx={{ minWidth: "90%" }}>
                    <SoftInput
                      placeholder={`Something for ${user.label}`}
                      type="text"
                      name="playerValue"
                      onChange={(e) => handleChange(e, index)}
                      value={user.assignedValue}
                      sx={{
                        minWidth: "100%",
                        // light glass input
                        border: "1px solid #E4E7EC",
                        backgroundColor: "#F6F7F9 !important",
                        color: "#1d2939 !important",
                        "& .MuiInputBase-input::placeholder": {
                          color: "#98A2B3 !important",
                          opacity: "1",
                        },
                        "&.Mui-focused": {
                          borderColor: primaryColor,
                          boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                        },
                      }}
                    />
                  </SoftBox>
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" 
                    style={{ cursor: "pointer" }} onClick={() => {
                      if (user.assignedValue) {
                        // Temporarily remove from display and mark for deletion
                        setUsersMarkedForDeletion((prev) => [...prev, user._id]);
                        setUsers((prevUsers) => prevUsers.filter((u) => u._id !== user._id));
                      } else {
                        // If no assignedValue, remove immediately
                        handleRemove(widget._id, user._id, false);
                      }
                    }}>
                    <rect width="17" height="17" rx="8.5" fill="#0F172A" fillOpacity="0.03"/>
                    <rect x="0.22356" y="0.22356" width="16.5529" height="16.5529" rx="8.27644" stroke="#0F172A" strokeOpacity="0.12" strokeWidth="0.44712"/>
                    <path d="M11.9385 4.11328C12.2004 3.85138 12.6248 3.85136 12.8867 4.11328C13.1485 4.37519 13.1485 4.79962 12.8867 5.06152L9.44825 8.49902L12.8867 11.9375C13.1485 12.1994 13.1485 12.6248 12.8867 12.8867C12.6248 13.1486 12.1994 13.1486 11.9375 12.8867L8.50001 9.44824L5.06251 12.8867C4.80067 13.1484 4.37613 13.1483 4.11427 12.8867C3.85236 12.6248 3.85236 12.1994 4.11427 11.9375L7.55079 8.5L4.11329 5.06152C3.85164 4.79965 3.85166 4.37516 4.11329 4.11328C4.3752 3.85135 4.8006 3.85135 5.06251 4.11328L8.50001 7.55078L11.9385 4.11328Z" fill="#475467"/>
                  </svg>
                </SoftBox>
              </Grid>
            </Grid>
          ))}
        </SoftBox>
      </Collapse>
      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap="1rem"
        sx={{ marginTop: "2rem" }}
      >
        <SoftButton
          onClick={closeModal}
          disabled={loading}
          loading={loading}
          sx={{
            width: "100%",
            borderRadius: "4px",
            // light glass secondary button
            border: "1px solid rgba(15,23,42,0.12)",
            boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
            textTransform: "none",
            background: "rgba(15,23,42,0.03)",
            color: "#475467",
            "&:hover": {
              background: "rgba(15,23,42,0.06)",
            },
          }}
        >
          Cancel
        </SoftButton>

        <SoftButton
          color="primary"
          type="submit"
          sx={{
            width: "100%",
            textTransform: "none",
            background: "#F9FAFB",
            boxShadow: "0px 0.320293px 0.640586px rgba(0, 0, 0, 0.05)",
            borderRadius: "2px",
            color: "#1D2939",
            ":hover": {
              background: "#F9FAFB",
            },
            ":disabled": {
              background: "#F9FAFB",
              color: "#CED4DA",
            },
          }}
          loading={loading}
        >
          {forUpdate ? "Update" : "Activate"}
        </SoftButton>
      </SoftBox>
    </form>
  );
};

export default MyLevel;
